import {useRecoilState} from "recoil";
import {
    BILLING_MODES,
    CATEGORY_LIST,
    DEPOSIT_CODES_LIST,
    GUARANTEE_SCHEMES,
    INVOICE_RECIPIENTS,
    PAYMENT_DUE_DATES,
    PAYMENT_METHDOS,
    PAYMENT_TIMINGS,
    PRODUCT_STATUS_LIST,
    SEGMENT_LIST
} from "./SalesMarketingRecoil";
import {useSalesMarketingApi} from "./useSalesMarketingApi";
import qs from "qs";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import {useContext, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";

const TENANT_LANGUAGE_MAP: Record<string, string> = {
    'drivalia_it': 'it',
    'drivalia_fr': 'fr',
    'drivalia_es': 'es',
    'drivalia_pt': 'pt',
};

export const useProductCodeActions = () => {
    const {globalData} = useContext(GlobalContext);
    const pendingCategoryPromises = useRef(new Map<string, Promise<any>>());
    const {GET, POST, PUT} = useSalesMarketingApi();
    const {t, i18n} = useTranslation('salesmarketing');
    const [segments, setSegments] = useRecoilState(SEGMENT_LIST);
    const [categoriesMap, setCategoriesMap] = useRecoilState(CATEGORY_LIST);
    const [depositCodes, setDepositCodes] = useRecoilState(DEPOSIT_CODES_LIST)
    const [productStatusList, setProductStatusList] = useRecoilState(PRODUCT_STATUS_LIST);
    const [paymentMethods, setPaymentMethods] = useRecoilState(PAYMENT_METHDOS);
    const [guaranteeSchemes, setGuaranteeSchemes] = useRecoilState(GUARANTEE_SCHEMES);
    const [paymentDueDates, setPaymentDueDates] = useRecoilState(PAYMENT_DUE_DATES);
    const [invoiceRecipients, setInvoiceRecipients] = useRecoilState(INVOICE_RECIPIENTS);
    const [billingModes, setBillingModes] = useRecoilState(BILLING_MODES);
    const [paymentTimings, setPaymentTimings] = useRecoilState(PAYMENT_TIMINGS);
    const {globalData: {user}} = useContext(GlobalContext);

    async function listSegment() {
        if (segments && segments.length !== 0) {
            return segments;
        } else {
            let data = await GET('sm/lookup/segment-code', true, null);
            let list = data?.result["segment-code"];
            setSegments(list);
            return list;
        }
    }

    async function getPreAuthorizationList(legalEntity: string, dealer: string) {
        let params = {legalEntities: [legalEntity], dealers: [dealer]};
        let data = await GET('sm/fnnanag/pre-auth/repository?offset=0&limit=10&sort=id&order=desc', true, {
            params: params,
            paramsSerializer: (params: any) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
        let list = data?.result.content;
        setDepositCodes(list);
        return list;

    }

    async function listCategory(legalEntity: string) {
        if (categoriesMap[legalEntity]) {
            return categoriesMap[legalEntity];
        }

        if (pendingCategoryPromises.current.has(legalEntity)) {

            return pendingCategoryPromises.current.get(legalEntity);
        }
        const promise = GET(`sm/lookup/category-code?tenantId=${legalEntity}`, true, null)
            .then(data => {
                const list = data?.result["category-code"];
                setCategoriesMap(prevMap => ({
                    ...prevMap,
                    [legalEntity]: list
                }));
                return list;
            })
            .finally(() => {
                pendingCategoryPromises.current.delete(legalEntity);
            });
        pendingCategoryPromises.current.set(legalEntity, promise);
        return promise;
    }

    async function listProductStatus() {
        if (productStatusList && productStatusList.length !== 0) {
            return productStatusList;
        } else {
            let data = await GET('sm/lookup/product-status', true, null);
            let list = data?.result["product-status"];
            setProductStatusList(list);
            return list;
        }
    }

    async function listPaymentMethods(legalEntity: string, dealer: string) {
        let params = {legalEntities: [legalEntity], dealers: [dealer]};
        let data = await GET('sm/fnnanag/payment-methods?domain=SM', true, {
            params: params,
            paramsSerializer: (params: any) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
        let list = data?.result;
        setPaymentMethods(list);
        return list;

    }

    async function listGuaranteeSchemes(legalEntity: string, dealer: string) {
        let params = {legalEntities: [legalEntity], dealers: [dealer]};
        let data = await GET('sm/fnnanag/guarantee-scheme-codes', true, {
            params: params,
            paramsSerializer: (params: any) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
        let list = data?.result;
        setGuaranteeSchemes(list);
        return list;

    }

    async function listPaymentDueDates(legalEntity: string, dealer: string) {

        let params = {legalEntities: [legalEntity], dealers: [dealer]};
        let data = await GET('sm/fnnanag/invoice-scheme?domain=SM', true, {
            params: params,
            paramsSerializer: (params: any) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
        let list = data?.result;
        setPaymentDueDates(list);
        return list;
    }

    async function listPaymentTimings() {
        if (paymentTimings && paymentTimings.length !== 0) {
            return paymentTimings;
        } else {
            let data = await GET('sm/lookup/payment-timing', true, null);
            let list = data?.result['payment-timing'];
            setPaymentTimings(list);
            return list;
        }
    }

    async function listInvoiceRecipients() {
        if (invoiceRecipients && invoiceRecipients.length !== 0) {
            return invoiceRecipients;
        } else {
            let data = await GET('sm/lookup/invoice-recipient', true, null);
            let list = data?.result['invoice-recipient'];
            setInvoiceRecipients(list);
            return list;
        }
    }
    async function listBillingModes(tenantId: string) {
        const language = tenantId ? (TENANT_LANGUAGE_MAP[tenantId] ?? 'en') : 'en';
        if (!i18n.hasResourceBundle(language, 'salesmarketing')) {
            await new Promise<void>((resolve) => {
                i18n.reloadResources([language], ['salesmarketing'], () => resolve());
            });
        }
        const tForTenant = i18n.getFixedT(language, 'salesmarketing');
        const list = [
            { label: tForTenant('product.createForm.billingModes.S'), value: 'S' },
            { label: tForTenant('product.createForm.billingModes.D'), value: 'D' }
        ];
        setBillingModes(prev => ({ ...prev, [tenantId]: list }));
        return list;
    }

    async function createProductCode(values: any) {
        let payload = {
            tenantId: values.tenantId !== '' ? values.tenantId : globalData?.user?.legalEntities[0]?.name,
            dealerId: values.dealerId !== '' ? values.dealerId : globalData?.user?.dealers[0]?.name,
            name: values.name,
            segmentCode: values.segment,
            categoryCode: values.category,
            description: values.description ? values.description : undefined,
            startDate: values.startDate ? values.startDate : undefined,
            endDate: values.endDate ? values.endDate : undefined,
            fastLine: values.fastLine ? values.fastLine : undefined,
            preAuthCode: values.preAuthCode ? values.preAuthCode : undefined,
            tax_configuration: values.tax_configuration ? values.tax_configuration : undefined,
            paymentMethod: [{
                value: paymentMethods.find((el) => {
                    return el.value === values.paymentMethod
                })?.label,
                id: values.paymentMethod
            }],
            paymentDueDate: [{
                value: paymentDueDates.find((el) => {
                    return el.value === values.paymentDueDate
                })?.label,
                id: values.paymentDueDate
            }],
            guaranteeSchemeCode: [{
                value: guaranteeSchemes.find((el) => {
                    return el.value === values.guarantee
                })?.label,
                id: values.guarantee
            }],
            firstInvoiceRecipient: [{
                value: invoiceRecipients.find((el) => {
                    return el.id === values.firstRecipient
                })?.recipientType,
                id: values.firstRecipient
            }],
            subsequentInvoiceRecipient: [{
                value: invoiceRecipients.find((el) => {
                    return el.id === values.subsequentRecipient
                })?.recipientType,
                id: values.subsequentRecipient
            }],
            paymentTiming: values.paymentTiming ? values.paymentTiming : undefined,
            billingMode: [{
                value: (billingModes[values.tenantId]).find((el) => {
                    return el.value === values.billingMode
                })?.label,
                id: values.billingMode
            }]
        };
        let data = await POST('sm/product-code/', payload, true, null);
        return data;
    }

    const findSortName = (columnName: string) => {
        if (columnName === "segment") {
            return "segmentCode";
        } else if (columnName === "category" || columnName === "category.categoryLabel") {
            return "categoryCode";
        } else if (columnName === "guaranteeScheme") {
            return "guaranteeSchemeCode";
        } else if (columnName === "status") {
            return "statusCode";
        } else {
            return columnName;
        }
    }

    async function listProductCode(filters: any) {
        let params = {
            tenantId: filters.legalEntity ? filters.legalEntity : undefined,
            dealerId: filters.dealer ? filters.dealer : undefined,
            productCode: filters.productCode ? filters.productCode : undefined,
            segmentCode: filters.segment ? filters.segment : undefined,
            categoryCode: filters.category ? filters.category : undefined,
            statusCode: filters.status ? filters.status : undefined,
            search: filters.search ? filters.search : undefined,
            name: filters.name ? filters.name : undefined,
            offset: filters.offset,
            limit: filters.limit,
            sort: filters.sort ? findSortName(filters.sort) : undefined,
            order: filters.order ? filters.order : undefined
        };
        let data = await GET('sm/product-code', false, {
            params: params,
            paramsSerializer: (params: any) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
        let formattedData = {
            totalElements: data?.result?.totalElements,
            content: new Array<any>()
        };
        data?.result?.content?.forEach((el: any) => {
            formattedData.content.push({
                tenantId: el.tenantId,
                dealerId: el.dealerId,
                productCode: el.productCode,
                segment: segments.find((s) => (s.segmentCode === el.segmentCode))?.segmentName,
                category: (categoriesMap[el.tenantId] || []).find(c => c.categoryCode === el.categoryCode)?.categoryLabel,
                name: el.name,
                description: el.description,
                startDate: el.startDate ? new Date(el.startDate) : null,
                endDate: el.endDate ? new Date(el.endDate) : null,
                guaranteeScheme: el.guaranteeSchemeCode ? el.guaranteeSchemeCode[0].value : null,
                status: productStatusList.find((s) => (s.statusCode === el.statusCode))?.statusName,
                paymentMethod: el.paymentMethod ? el.paymentMethod[0].value : null,
                paymentDueDate: el.paymentDueDate ? el.paymentDueDate[0].value : null,
                billingMode: el.billingMode ? el.billingMode[0].value : null,
                firstInvoiceRecipient: el.firstInvoiceRecipient ? el.firstInvoiceRecipient[0].value : null,
                subsequentInvoiceRecipient: el.subsequentInvoiceRecipient ? el.subsequentInvoiceRecipient[0].value : null,
                paymentTiming: paymentTimings.find((t) => (t.id === el.paymentTiming))?.value,
                fastLine: el.fastLine ? el.fastLine : false,
                tax_configuration: el.tax_configuration ? el.tax_configuration : null,
                preAuthCode: el.preAuthCode ? el.preAuthCode : null
            })
        });
        return formattedData;
    }

    async function findProductCode(productCode: string) {
        let params = {productCode: productCode};
        let data = await GET('sm/product-code', true, {params: params});
        let detail = data?.result?.content[0];
        detail.tenantId = detail?.tenantId ? detail?.tenantId : '';
        detail.dealerId = detail?.dealerId ? detail?.dealerId : '';
        detail.paymentMethod = detail?.paymentMethod ? detail?.paymentMethod[0]?.id : '';
        detail.paymentDueDate = detail?.paymentDueDate ? detail?.paymentDueDate[0]?.id : '';
        detail.guarantee = detail?.guaranteeSchemeCode ? detail?.guaranteeSchemeCode[0]?.id : '';
        detail.firstRecipient = detail?.firstInvoiceRecipient ? detail?.firstInvoiceRecipient[0].id : '';
        detail.subsequentRecipient = detail?.subsequentInvoiceRecipient ? detail?.subsequentInvoiceRecipient[0]?.id : '';
        detail.billingMode = detail?.billingMode ? detail?.billingMode[0]?.id : '';
        return detail;
    }

    async function updateProductCode(values: any, id: any) {
        let payload = {
            tenantId: values.tenantId,
            dealerId: values.dealerId,
            id: id,
            productCode: values.productCode,
            statusCode: values.status,
            preAuthCode: values.preAuthCode,
            name: values.name,
            segmentCode: values.segment,
            categoryCode: values.category,
            description: values.description ? values.description : undefined,
            startDate: values.startDate ? values.startDate : undefined,
            endDate: values.endDate ? values.endDate : undefined,
            fastLine: values.fastLine ? values.fastLine : undefined,
            tax_configuration: values.tax_configuration ? values.tax_configuration : undefined,
            paymentMethod: [{
                value: paymentMethods.find((el) => {
                    return el.value === values.paymentMethod
                })?.label,
                id: values.paymentMethod
            }],
            paymentDueDate: [{
                value: paymentDueDates.find((el) => {
                    return el.value === values.paymentDueDate
                })?.label,
                id: values.paymentDueDate
            }],
            guaranteeSchemeCode: [{
                value: guaranteeSchemes.find((el) => {
                    return el.value === values.guarantee
                })?.label,
                id: values.guarantee
            }],
            firstInvoiceRecipient: [{
                value: invoiceRecipients.find((el) => {
                    return el.id === values.firstRecipient
                })?.recipientType,
                id: values.firstRecipient
            }],
            subsequentInvoiceRecipient: [{
                value: invoiceRecipients.find((el) => {
                    return el.id === values.subsequentRecipient
                })?.recipientType,
                id: values.subsequentRecipient
            }],
            paymentTiming: values.paymentTiming ? values.paymentTiming : undefined,
            billingMode: [{
                value: (billingModes[values.tenantId]).find((el) => {
                    return el.value === values.billingMode
                })?.label,
                id: values.billingMode
            }]
        };
        let data = PUT('sm/product-code/', payload, true, null);
        return data;
    }

    async function listProductByCategoryCodeList(categoryCodeList: any[]) {
        let data = await GET('sm/product-code/ext', false,
            {
                params: {
                    categoryCodeList: categoryCodeList,
                    sort: "statusCode",
                    order: "ASC"
                },
                paramsSerializer: (params: any) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
            });
        return data?.result?.content;
    }


    return {
        listSegment,
        listCategory,
        listProductStatus,
        listPaymentMethods,
        listGuaranteeSchemes,
        listPaymentDueDates,
        listPaymentTimings,
        listInvoiceRecipients,
        listBillingModes,
        createProductCode,
        listProductCode,
        findProductCode,
        updateProductCode,
        listProductByCategoryCodeList,
        getPreAuthorizationList
    }


}